import {
  useApolloClient,
  type ApolloClient,
  type NormalizedCacheObject,
} from '@apollo/client';
import type { SearchResultJobV5 } from '@seek/chalice-types';
import { useMelwaysInfo } from '@seek/melways-react';
import { metrics } from '@seek/metrics-js';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo,
} from 'react';
import { v4 as uuid } from 'uuid';

import useJobDetail from 'src/components/SearchResultPage/SplitView/JobDetail/useJobDetail';
import {
  persistJobUrl,
  removePersistedJobUrl,
} from 'src/components/SearchResultPage/SplitView/helpers';
import { useAppConfig } from 'src/config/appConfig';
import type { HubbleJDVEventTriggerTag } from 'src/hooks/trackJobDetailsLoaded';
import { useAnalyticsFacade } from 'src/modules/AnalyticsFacade';
import {
  useEnableJdvJobMode,
  useEnableNumberOfApplicants,
} from 'src/store/featureFlags/hooks';
import {
  fetchUnifiedJob,
  fetchUnifiedJobPersonalised,
  setJobDetailsCorrelationId,
} from 'src/store/jobdetails';
import { setSelectedJobIdAction } from 'src/store/results';
import {
  selectAuthenticated,
  selectJobDetailsResult,
  selectSelectedJobId,
  selectSessionId,
  selectSolId,
  selectXRealIp,
} from 'src/store/selectors';
import { useMelwaysLocale, useMelwaysZone } from 'src/utils/melwaysHelper';

import { useDispatch, useSelector } from '../store/react';

import useShouldFetchJobDetails from './useShouldFetchJobDetails';

const mapEventTriggerToJobListingPosition = (
  trigger: HubbleJDVEventTriggerTag,
) => {
  switch (trigger) {
    case 'pre_selected':
      return -1;
    case 'auto_display':
      return 0;
    default:
      return undefined;
  }
};

const useFetchJobDetails = (job?: SearchResultJobV5) => {
  const dispatch = useDispatch();
  const analyticsFacade = useAnalyticsFacade();
  const currentJobDetails = useSelector(selectJobDetailsResult);
  const jobseekerSessionId = useSelector(selectSessionId);
  const authenticated = useSelector(selectAuthenticated);
  const { country: countryCode } = useAppConfig();
  const { language: languageCode } = useMelwaysInfo();
  const locale = useMelwaysLocale();
  const zone = useMelwaysZone();
  const apolloClient = useApolloClient() as ApolloClient<NormalizedCacheObject>;
  const shouldFetchJobDetails = useShouldFetchJobDetails(job);
  const ipAddress = useSelector(selectXRealIp);

  const enableWorkArrangements = useEnableJdvJobMode();

  const enableApplicantCount = useEnableNumberOfApplicants() && authenticated;
  const visitorId = useSelector(selectSolId);

  const fetchJobDetails = useCallback(() => {
    // Spltiview JDV is rendered client-side
    if (ENV.SERVER || !job) {
      return Promise.resolve(undefined);
    }

    const jobId = job.id.toString();

    dispatch(
      fetchUnifiedJob({
        apolloClient,
        analyticsFacade,
        jobId,
        sessionId: jobseekerSessionId,
        zone,
        locale,
        languageCode,
        shouldThrowError: false,
        countryCode,
        xRealIp: ipAddress,
        enableApplicantCount,
        enableWorkArrangements,
        visitorId,
      }),
    );

    if (authenticated) {
      dispatch(
        fetchUnifiedJobPersonalised({
          apolloClient,
          id: jobId,
          sessionId: jobseekerSessionId,
          languageCode,
          locale,
          zone,
          xRealIp: ipAddress,
          visitorId,
        }),
      );
    }
  }, [
    job,
    dispatch,
    apolloClient,
    analyticsFacade,
    jobseekerSessionId,
    zone,
    locale,
    languageCode,
    countryCode,
    authenticated,
    ipAddress,
    enableApplicantCount,
    enableWorkArrangements,
    visitorId,
  ]);

  useEffect(() => {
    if (shouldFetchJobDetails) {
      dispatch(setJobDetailsCorrelationId(uuid()));
      fetchJobDetails();
    }
  }, [shouldFetchJobDetails, fetchJobDetails, dispatch]);

  return {
    currentJobDetails,
    fetchJobDetails,
  };
};

export const useSplitView = () => {
  const [selectedJob, setSelectedJob] = useState<SearchResultJobV5>();
  const dispatch = useDispatch();
  const [displayEventTrigger, setDisplayEventTrigger] =
    useState<HubbleJDVEventTriggerTag>('default');
  const { currentJobDetails, fetchJobDetails } =
    useFetchJobDetails(selectedJob);
  const selectedJobId = useSelector(selectSelectedJobId);
  const { country: countryCode } = useAppConfig();

  const setSelectedJobAndDisplayEventTrigger = useCallback(
    (job?: SearchResultJobV5, eventTrigger?: HubbleJDVEventTriggerTag) => {
      setSelectedJob(job);
      metrics.count('pageload');

      if (job) {
        persistJobUrl({ job, dispatch });
      } else if (selectedJobId) {
        dispatch(setSelectedJobIdAction(undefined));
        removePersistedJobUrl(dispatch);
      }
      setDisplayEventTrigger(eventTrigger ?? 'default');
    },
    [dispatch, selectedJobId],
  );

  // Apollo's loading state is not firing correctly, so we need to use the store state instead
  // Will be removed once Apollo's loading state is fixed
  const { isLoading } = useJobDetail();

  // Memoize clearSelectedJob to maintain stable reference
  const clearSelectedJob = useCallback(() => {
    setSelectedJob(undefined);
    dispatch(setSelectedJobIdAction(undefined));
  }, [dispatch]);

  // Memoize the entire return object to maintain stable context value
  return useMemo(
    () => ({
      selectedJob,
      setSelectedJob: setSelectedJobAndDisplayEventTrigger,
      clearSelectedJob,
      selectedJobPosition:
        mapEventTriggerToJobListingPosition(displayEventTrigger) ??
        Number(selectedJob?.solMetadata.sectionRank),
      eventTrigger: displayEventTrigger,
      currentJobDetails,
      fetchJobDetails,
      isLoading,
      countryCode,
    }),
    [
      selectedJob,
      setSelectedJobAndDisplayEventTrigger,
      clearSelectedJob,
      displayEventTrigger,
      currentJobDetails,
      fetchJobDetails,
      isLoading,
      countryCode,
    ],
  );
};

type SplitviewContextType = ReturnType<typeof useSplitView>;

const SplitViewContext = createContext<SplitviewContextType>(
  {} as SplitviewContextType,
);

export const SplitViewContextProvider = SplitViewContext.Provider;

export const useSplitViewContext = () => useContext(SplitViewContext);
